/**
 * Section des relevés d'index
 */

import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Assessment, Add as AddIcon } from "@mui/icons-material";
import IndexReadingsTable from "../tables/IndexReadings";
import { COLORS } from "../../constants/colors";
import { putData } from "../../API/Put";

/**
 * Section des relevés d'index pour compteurs ADE_pulse uniquement
 * @param {Object} props
 * @param {Object} props.devicesByType - Devices groupés par type
 * @param {Object} props.indexReadings - Relevés d'index par device
 * @param {Function} props.onAddReading - Callback pour ajouter un relevé
 * @param {Function} props.onEditReading - Callback pour éditer un relevé
 * @param {Function} props.onDeviceUpdate - Callback appelé après mise à jour du device (ex: pulse_multiplier)
 * @param {boolean} props.isMobile - Mode mobile
 */

// Constante pour identifier les devices ADE_PULSE
const ADE_PULSE_BRAND_ID = 25;

// Options du poids d'impulsion
const PULSE_WEIGHT_OPTIONS = [
  { value: 1, label: "x1" },
  { value: 10, label: "x10" },
  { value: 100, label: "x100" },
];

const IndexReadingsSection = ({
  devicesByType,
  indexReadings,
  onAddReading,
  onEditReading,
  onDeviceUpdate,
  isMobile,
}) => {
  // État pour gérer le chargement lors de la mise à jour du poids d'impulsion
  const [updatingDevice, setUpdatingDevice] = useState(null);

  // État local pour tracker les pulse_multiplier de chaque device
  const [localPulseWeights, setLocalPulseWeights] = useState({});

  // Filtrer uniquement les devices ADE_pulse (par brand_id ou par nom en fallback)
  const adePulseDevices = devicesByType
    ? Object.values(devicesByType)
        .flat()
        .filter(
          (d) =>
            d.device_brand_id === ADE_PULSE_BRAND_ID ||
            d.device_brand?.id === ADE_PULSE_BRAND_ID ||
            d.name?.toLowerCase().includes("ade_pulse")
        )
    : [];

  // Initialiser les poids d'impulsion depuis les devices au chargement
  useEffect(() => {
    const initialWeights = {};
    adePulseDevices.forEach((device) => {
      if (device.pulse_multiplier && !localPulseWeights[device.dev_eui]) {
        initialWeights[device.dev_eui] = device.pulse_multiplier;
      }
    });

    if (Object.keys(initialWeights).length > 0) {
      setLocalPulseWeights((prev) => ({ ...prev, ...initialWeights }));
    }
  }, [devicesByType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vérifier s'il y a des devices
  if (!devicesByType || Object.keys(devicesByType).length === 0) {
    return null;
  }

  // Gestion du changement de poids d'impulsion
  const handlePulseWeightChange = async (device, newValue) => {
    setUpdatingDevice(device.dev_eui);
    try {
      const response = await putData(`/devices/${device.dev_eui}`, {
        pulse_multiplier: newValue,
      });
      if (response?.status === 200) {
        // Mise à jour locale immédiate
        setLocalPulseWeights((prev) => ({
          ...prev,
          [device.dev_eui]: newValue,
        }));
        if (onDeviceUpdate) {
          onDeviceUpdate(device.dev_eui, { pulse_multiplier: newValue });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du poids d'impulsion:", error);
    } finally {
      setUpdatingDevice(null);
    }
  };

  // Ne pas afficher la section s'il n'y a pas de device ADE_pulse
  if (adePulseDevices.length === 0) {
    return null;
  }

  // Trouver le premier device ADE_pulse pour le bouton d'ajout
  const findFirstDevice = () => {
    return adePulseDevices[0];
  };

  // Vérifier si tous les relevés sont vides
  const hasNoReadings = Object.values(indexReadings).every(
    (readings) => !readings || readings.length === 0
  );

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Card
          sx={{
            mt: 2,
            mb: 2,
            borderRadius: "6px !important",
            border: `1px solid ${COLORS.CARD_BORDER}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            backgroundColor: COLORS.CARD_BACKGROUND,
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            {/* En-tête */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 1.5 : 0,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Assessment sx={{ color: COLORS.PRIMARY, fontSize: 28 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: isMobile ? "1.1rem" : "1.25rem",
                      fontWeight: 600,
                    }}
                  >
                    Relevés d'index
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.85rem" }}
                  >
                    Historique des relevés manuels sur les compteurs
                  </Typography>
                </Box>
              </Box>

              {/* Bouton d'ajout */}
              {Object.values(devicesByType).flat().length > 0 && (
                <Button
                  variant="contained"
                  color="success"
                  size={isMobile ? "small" : "medium"}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const firstDevice = findFirstDevice();
                    if (firstDevice) {
                      onAddReading(firstDevice.dev_eui);
                    }
                  }}
                  sx={{
                    fontWeight: "medium",
                    px: isMobile ? 2 : 3,
                    width: isMobile ? "100%" : "auto",
                    textTransform: "none",
                  }}
                >
                  {isMobile ? "Ajouter" : "Ajouter un relevé"}
                </Button>
              )}
            </Box>

            {/* Liste des devices ADE_pulse avec sélecteur de poids d'impulsion */}
            {adePulseDevices.map((device) => {
              const deviceReadings = indexReadings[device.dev_eui] || [];
              // Utiliser la valeur locale si elle existe, sinon celle du device, sinon 1
              const currentPulseWeight =
                localPulseWeights[device.dev_eui] ??
                device.pulse_multiplier ??
                1;
              const isUpdating = updatingDevice === device.dev_eui;

              return (
                <Box
                  key={device.dev_eui}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${COLORS.CARD_BORDER_LIGHT}`,
                    backgroundColor: COLORS.CARD_BACKGROUND_DARK,
                  }}
                >
                  {/* En-tête du device avec nom et sélecteur de poids d'impulsion */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? 1.5 : 2,
                      mb: deviceReadings.length > 0 ? 1.5 : 0,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: COLORS.PRIMARY,
                        fontSize: isMobile ? "0.95rem" : "1rem",
                      }}
                    >
                      {device.name || device.dev_eui}
                    </Typography>

                    {/* Sélecteur de poids d'impulsion */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.85rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Poids d'impulsion :
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={currentPulseWeight}
                          onChange={(e) =>
                            handlePulseWeightChange(device, e.target.value)
                          }
                          disabled={isUpdating}
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                          sx={{
                            backgroundColor: "rgba(0,0,0,0.2)",
                            fontSize: "0.85rem",
                            "& .MuiSelect-select": {
                              py: 0.75,
                              px: 1.5,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.1)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: COLORS.PRIMARY,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: COLORS.PRIMARY,
                            },
                          }}
                        >
                          {PULSE_WEIGHT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isUpdating && (
                        <CircularProgress size={18} sx={{ color: COLORS.PRIMARY }} />
                      )}
                    </Box>
                  </Box>

                  {/* Table des relevés (uniquement si des relevés existent) */}
                  {deviceReadings.length > 0 && (
                    <IndexReadingsTable
                      readings={deviceReadings}
                      selectedItems={[]}
                      setSelectedItems={() => {}}
                      setSelectedRows={() => {}}
                      onRowClick={(_, rowMeta) => {
                        const clickedReading = deviceReadings[rowMeta.rowIndex];
                        onEditReading(device.dev_eui, clickedReading);
                      }}
                    />
                  )}
                </Box>
              );
            })}

            {/* Message si aucun relevé */}
            {hasNoReadings && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  px: 2,
                  borderRadius: 1,
                  border: "1px dashed rgba(255,255,255,0.1)",
                  backgroundColor: "rgba(0,0,0,0.1)",
                }}
              >
                <Assessment
                  sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Aucun relevé d'index enregistré pour le moment.
                </Typography>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  Cliquez sur "Ajouter un relevé" pour commencer.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default IndexReadingsSection;
